import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, withKeys as _withKeys, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["inert", "aria-busy"]
const _hoisted_2 = ["aria-busy"]
const _hoisted_3 = { class: "quest-file-toolbar" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 1,
  class: "quest-empty"
}
const _hoisted_7 = ["value"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 2,
  class: "load-error",
  role: "alert"
}
const _hoisted_10 = ["inert"]
const _hoisted_11 = {
  key: 0,
  class: "settings-error",
  role: "alert"
}

import StudioSelectField from "../StudioSelectField.vue";
import { inject, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import { toast } from "vue-sonner";
import SectionLayout from "@/components/Layout/SectionLayout.vue";
import StudioHistoryToolbar from "../StudioHistoryToolbar.vue";
import { useStudioDocumentHistory } from "../useStudioHistory";
import type { StudioEditorKind } from "../studioSidebar";
import { StorageClass } from "@/services/storage/storage";
import { downloadTextFile } from "@/utils/download";
import { ProjectID } from "../../constant/constant";
import QuestPanel from "./QuestPanel.vue";
import type { QuestProject } from "./types";
import { createQuestProject, decodeQuestProject, encodeQuestProject, validateQuestProject } from "./questProject";
import { useWorkspaceStructIds } from "../useWorkspaceStructIds";
import { exportQuestVariables } from "./questExporter";
import { createWorkspaceSaveQueue } from "./workspaceSaveQueue";
import RuntimeImportButton from "../RuntimeImportButton.vue";
import { commitRuntimeImport } from "../runtimeImportStorage";
import { importQuest } from "./questImporter";


export default /*@__PURE__*/_defineComponent({
  __name: 'QuestEditor',
  props: {
    editorKind: { default: "Quest" }
  },
  emits: ["update:editorKind"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const emit = __emit;
const storage = inject<StorageClass>("storage")!;
const workspace = inject<Ref<string>>("selectedWorkspaceId")!;
// 工作区切换会重建 Panel；所有异步保存固定使用原工作区路径。
const workspaceId = workspace.value;
const documentPath = `/${workspaceId}/QuestEditor.json`;
const legacyDirectory = `/${workspaceId}/QuestEditor`;
const downloadBaseName = workspaceId.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_") || "工作区";
const project = ref<QuestProject>();
const legacyFiles = ref<string[]>([]);
const legacySelection = ref("");
const loadError = ref("");
const busy = ref(false);
const exporting = ref(false);
const saveStatus = ref("准备读取任务");
const settingsOpen = ref(false);
const workspaceIds = useWorkspaceStructIds();
const unassignedDraft = ref(-1);
const settingsError = ref("");
let disposed = false;
let loading = false;
let requestId = 0;
let revision = 0;
const historyElement = ref<HTMLElement>();
const history = useStudioDocumentHistory({
  project, element: historyElement, blocked: () => busy.value, label: "修改任务",
  ownsTarget: target => !!target.closest?.(".quest-settings"),
  afterRestore: () => { settingsOpen.value = false; },
  onError: error => { console.error(error); toast.error(error instanceof Error ? error.message : "任务恢复失败"); },
});

function showError(error: unknown, fallback: string) {
  console.error(error);
  toast.error(error instanceof Error ? error.message : fallback);
}
const saveQueue = createWorkspaceSaveQueue(async (path, data) => {
  const version = revision;
  await storage.setProject(ProjectID).writeFile(path, data);
  if (!disposed && version === revision) saveStatus.value = "已自动保存";
}, (error) => { saveStatus.value = "保存失败"; showError(error, "任务保存失败"); });
__expose({ prepareToLeave: () => {
  if (busy.value) return Promise.reject(new Error("任务数据正在读写，请稍后切换"));
  settingsOpen.value = false;
  return saveQueue.flush();
} });

watch(project, () => {
  if (loading || disposed || !project.value) return;
  revision++;
  saveStatus.value = "待保存…";
  saveQueue.schedule(documentPath, encodeQuestProject(project.value));
}, { deep: true, flush: "sync" });

async function loadProject(legacyFile?: string) {
  if (busy.value || disposed || project.value) return;
  const request = ++requestId;
  const active = () => !disposed && request === requestId;
  busy.value = true;
  loadError.value = "";
  saveStatus.value = "正在读取任务…";
  try {
    let loaded: QuestProject;
    let migratedFrom = "";
    let status = "已加载";
    const exists = await storage.setProject(ProjectID).exists(documentPath);
    if (!active()) return;
    if (exists) {
      // 已有固定任务配置时只读这一份，不能因读取失败而用空配置或旧备份覆盖它。
      loaded = decodeQuestProject(await storage.setProject(ProjectID).readFile(documentPath));
    } else {
      const hasLegacyDirectory = await storage.setProject(ProjectID).exists(legacyDirectory);
      if (!active()) return;
      const entries = hasLegacyDirectory ? await storage.setProject(ProjectID).getFiles(legacyDirectory) : [];
      if (!active()) return;
      const candidates = entries.filter(name => /^[^/\\]+\.json$/i.test(name)).sort();
      legacyFiles.value = candidates;
      if (legacyFile !== undefined && !candidates.includes(legacyFile)) {
        throw new Error("选中的旧任务文件已不存在，请重新选择。");
      }
      if (legacyFile === undefined && candidates.length > 1) {
        saveStatus.value = "请选择要沿用的旧任务";
        return;
      }
      migratedFrom = legacyFile ?? candidates[0] ?? "";
      loaded = migratedFrom
        ? decodeQuestProject(await storage.setProject(ProjectID).readFile(`${legacyDirectory}/${migratedFrom}`))
        : createQuestProject();
      if (!active()) return;
      // 先保存固定配置，再开放编辑；旧文件仅作备份保留，不删除、不重编号、不合并。
      await storage.setProject(ProjectID).writeFile(documentPath, encodeQuestProject(loaded));
      status = migratedFrom ? "已沿用旧任务" : "已创建工作区任务";
    }
    if (!active()) return;
    loading = true;
    project.value = loaded;
    loading = false;
    legacyFiles.value = [];
    legacySelection.value = "";
    saveStatus.value = status;
    if (migratedFrom) toast.success(`已沿用「${migratedFrom}」，原文件已保留为备份`);
  } catch (error) {
    if (active()) {
      loadError.value = error instanceof Error ? error.message : "无法读取工作区任务，原数据已保留";
      saveStatus.value = "读取失败";
      showError(error, "无法读取工作区任务，原数据已保留");
    }
  } finally { if (active()) busy.value = false; }
}
async function exportVariables() {
  if (!project.value || exporting.value || disposed) return;
  const exportName = downloadBaseName;
  exporting.value = true;
  try {
    const result = exportQuestVariables(workspaceIds.quest(project.value));
    if (disposed) return;
    downloadTextFile(result.json, `${exportName}-任务配置数据.json`, "application/json");
    if (result.warnings.length) { console.warn(result.warnings); toast.warning(result.warnings.join("；")); }
    else toast.success("已导出任务配置数据 JSON");
  } catch (error) { showError(error, "任务导出失败"); }
  finally { exporting.value = false; }
}
async function importConfiguration(file: File) {
  if (busy.value || disposed || exporting.value) return;
  busy.value = true;
  try {
    const result = await commitRuntimeImport({ file, decode: importQuest, encode: encodeQuestProject,
      storage: storage.setProject(ProjectID), active: () => !disposed, flush: () => saveQueue.flush(),
      overwrite: { path: documentPath, backupDirectory: `/${workspaceId}/ImportBackups/Quest`,
        confirm: () => confirm("导入将覆盖当前工作区的全部任务配置。原配置会先备份，是否继续？") } });
    if (!result) return;
    loading = true; project.value = result.project; loading = false;
    settingsOpen.value = false; legacyFiles.value = []; legacySelection.value = ""; loadError.value = ""; saveStatus.value = "已导入";
    toast.success("任务配置已覆盖导入，原配置已备份");
  } finally { loading = false; if (!disposed) busy.value = false; }
}
function openSettings() {
  if (!project.value) return;
  unassignedDraft.value = project.value.unassignedChapterId;
  settingsError.value = "";
  settingsOpen.value = true;
}
function applySettings() {
  if (!project.value) return;
  const candidate = { ...project.value, unassignedChapterId: unassignedDraft.value };
  let errors: string[];
  try { errors = validateQuestProject(workspaceIds.quest(candidate)); }
  catch (error) { settingsError.value = error instanceof Error ? error.message : String(error); return; }
  if (errors.length) { settingsError.value = errors.join("；"); return; }
  project.value.unassignedChapterId = candidate.unassignedChapterId;
  settingsOpen.value = false;
}
async function changeEditor(kind: StudioEditorKind) {
  try { await saveQueue.flush(); emit("update:editorKind", kind); }
  catch { /* 保存失败时留在任务编辑器，防止丢失未保存内容。 */ }
}
onMounted(() => {
  void loadProject();
});
onBeforeUnmount(() => {
  disposed = true; requestId++;
  void saveQueue.flush().catch(() => undefined);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      ref_key: "historyElement",
      ref: historyElement,
      class: "quest-editor",
      inert: busy.value,
      "aria-busy": busy.value
    }, [
      _createVNode(SectionLayout, {
        title: "任务编辑",
        class: "quest-edit-section"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass(["quest-workspace", { 'is-busy': busy.value }]),
            "aria-busy": busy.value
          }, [
            _createElementVNode("header", _hoisted_3, [
              _createElementVNode("span", null, [
                _createTextVNode(_toDisplayString(_unref(workspaceId)) + " ", 1),
                _cache[8] || (_cache[8] = _createElementVNode("span", { class: "workspace-label" }, "/ 工作区任务", -1))
              ]),
              _createElementVNode("small", {
                role: "status",
                class: _normalizeClass({ 'save-error': saveStatus.value === '保存失败' })
              }, _toDisplayString(saveStatus.value), 3),
              _createVNode(StudioHistoryToolbar, {
                history: _unref(history),
                disabled: busy.value
              }, null, 8, ["history", "disabled"]),
              _createElementVNode("button", {
                type: "button",
                disabled: !project.value || busy.value,
                onClick: openSettings
              }, "任务设置", 8, _hoisted_4),
              _createVNode(RuntimeImportButton, {
                disabled: busy.value || exporting.value,
                "import-file": importConfiguration
              }, null, 8, ["disabled"]),
              _createElementVNode("button", {
                type: "button",
                class: "primary",
                disabled: !project.value || busy.value || exporting.value,
                onClick: exportVariables
              }, _toDisplayString(exporting.value ? '导出中…' : '导出千星任务'), 9, _hoisted_5)
            ]),
            (project.value)
              ? (_openBlock(), _createBlock(QuestPanel, {
                  key: 0,
                  project: project.value,
                  inert: busy.value,
                  "sidebar-disabled": busy.value
                }, null, 8, ["project", "inert", "sidebar-disabled"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (legacyFiles.value.length > 1)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[10] || (_cache[10] = _createElementVNode("h3", null, "选择要沿用的旧任务", -1)),
                        _cache[11] || (_cache[11] = _createElementVNode("p", null, "每个工作区现在只有一份任务配置。检测到多份旧文件，请选择一份沿用；其他原文件保留为备份，不会合并或删除。", -1)),
                        _createElementVNode("form", {
                          class: "legacy-choice",
                          onSubmit: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (loadProject(legacySelection.value)), ["prevent"]))
                        }, [
                          _createVNode(StudioSelectField, {
                            modelValue: legacySelection.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((legacySelection).value = $event)),
                            "aria-label": "沿用旧任务文件",
                            disabled: busy.value
                          }, {
                            default: _withCtx(() => [
                              _cache[9] || (_cache[9] = _createElementVNode("option", {
                                disabled: "",
                                value: ""
                              }, "请选择一份旧任务", -1)),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(legacyFiles.value, (file) => {
                                return (_openBlock(), _createElementBlock("option", {
                                  key: file,
                                  value: file
                                }, _toDisplayString(file), 9, _hoisted_7))
                              }), 128))
                            ]),
                            _: 1
                          }, 8, ["modelValue", "disabled"]),
                          _createElementVNode("button", {
                            type: "submit",
                            disabled: busy.value || !legacySelection.value
                          }, "沿用这份任务", 8, _hoisted_8)
                        ], 32)
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("h3", null, _toDisplayString(busy.value ? '正在打开工作区任务…' : '工作区任务暂未加载'), 1),
                        _cache[12] || (_cache[12] = _createElementVNode("p", null, "每个工作区只有一份任务配置，首次进入时自动创建。", -1))
                      ], 64)),
                  (loadError.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_9, [
                        _createTextVNode(_toDisplayString(loadError.value), 1),
                        _cache[13] || (_cache[13] = _createElementVNode("br", null, null, -1)),
                        _cache[14] || (_cache[14] = _createTextVNode("原数据不会被空配置覆盖。", -1))
                      ]))
                    : _createCommentVNode("", true),
                  (loadError.value && !busy.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 3,
                        type: "button",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (loadProject()))
                      }, "重新读取"))
                    : _createCommentVNode("", true)
                ]))
          ], 10, _hoisted_2)
        ]),
        _: 1
      })
    ], 8, _hoisted_1),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (settingsOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            inert: busy.value,
            class: "quest-settings-backdrop dsfg-typography",
            onClick: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (settingsOpen.value = false), ["self"])),
            onKeydown: _cache[7] || (_cache[7] = _withKeys(($event: any) => (settingsOpen.value = false), ["esc"]))
          }, [
            _createElementVNode("form", {
              class: "quest-settings",
              role: "dialog",
              "aria-modal": "true",
              "aria-label": "任务设置",
              onSubmit: _withModifiers(applySettings, ["prevent"])
            }, [
              _createElementVNode("header", null, [
                _cache[15] || (_cache[15] = _createElementVNode("h3", null, "任务设置", -1)),
                _createElementVNode("button", {
                  type: "button",
                  "aria-label": "关闭任务设置",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (settingsOpen.value = false))
                }, "×")
              ]),
              _createElementVNode("label", null, [
                _cache[16] || (_cache[16] = _createTextVNode("无章节编号", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((unassignedDraft).value = $event)),
                  "aria-label": "无章节编号",
                  type: "number",
                  step: "1"
                }, null, 512), [
                  [
                    _vModelText,
                    unassignedDraft.value,
                    void 0,
                    { number: true }
                  ]
                ]),
                _cache[17] || (_cache[17] = _createElementVNode("small", null, "直属主任务的“归属章节”字段使用此编号，默认 -1。", -1))
              ]),
              (settingsError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(settingsError.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("footer", null, [
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (settingsOpen.value = false))
                }, "取消"),
                _cache[18] || (_cache[18] = _createElementVNode("button", {
                  class: "primary",
                  type: "submit"
                }, "应用", -1))
              ])
            ], 32)
          ], 40, _hoisted_10))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})